import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import io from 'socket.io-client';
import { server_address } from '../api/Config';
import { groupReducer, initialGroupState } from './groupReducer';
import { useLanguage } from '../i18n/LanguageContext';
import { startKnockLoop, stopKnockLoop, stopAllKnockLoops, setSoundEffectsMuted } from '../utils/soundEffects';
import { RefreshToken, redirectToLogin } from '../api/TokenOperations';

const SocketContext = createContext(null);

let toastId = 0;
let notificationId = 0;

// Resolves once the socket actually reconnects (or after timeoutMs if it
// doesn't) - used by ackEmit's retry below so a re-emitted event isn't fired
// while the transport is still mid-handshake.
function waitForReconnect(socket, timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (socket.connected) return resolve(true);
    const timer = setTimeout(() => {
      socket.off('connect', onConnect);
      resolve(false);
    }, timeoutMs);
    const onConnect = () => {
      clearTimeout(timer);
      resolve(true);
    };
    socket.once('connect', onConnect);
  });
}

// The server's socket.io auth middleware deliberately never rejects a
// connection over a bad/expired JWT (kept permissive for a legacy
// unauthenticated-DM flow - see socketServer.js's io.use) - it just leaves
// socket.data.userId unset, so every authenticated handler comes back with
// {ok:false, code:'NOT_AUTHENTICATED'} instead. A socket that connected with
// a stale accessToken (the proactive refresh in SocketProvider's connection
// effect below covers the common "just opened the app" case, but not a
// session that's been open long enough for a fresh token to go stale again)
// would otherwise fail every single call forever with no way to recover
// short of a manual reload. Mirrors httpClient.js's apiRequest
// expired->refresh->retried:true pattern exactly, just for the socket path.
async function ackEmit(socketRef, event, payload, t, retried = false) {
  const socket = socketRef.current;
  if (!socket) return { ok: false, message: t ? t('errors.notConnected') : 'Not connected.' };

  const res = await new Promise((resolve) => socket.emit(event, payload, resolve));

  if (res && res.code === 'NOT_AUTHENTICATED' && !retried) {
    const refreshed = await RefreshToken();
    if (refreshed === 'expired') {
      redirectToLogin();
      return res;
    }
    if (refreshed !== 'ok') return res; // network hiccup - not worth a reconnect attempt
    // auth is a callback (see below), so this reconnect picks up the fresh
    // token automatically instead of resending the one that just failed.
    socket.disconnect();
    socket.connect();
    const reconnected = await waitForReconnect(socket);
    if (!reconnected) return res;
    return ackEmit(socketRef, event, payload, t, true);
  }

  return res;
}

export function SocketProvider({ children }) {
  const socketRef = useRef(null);
  const { t } = useLanguage();
  // The socket connection effect below must NOT depend on `t` - reconnecting
  // the whole socket just because the user switched languages would be a
  // bad trade. Read translations through this ref instead so handlers
  // always use the latest language without that dependency.
  const tRef = useRef(t);
  tRef.current = t;
  // Tracks actual socket.io connection state (not just whether the ref is
  // non-null - `io(...)` returns an object synchronously well before the
  // handshake completes). Consumers that fire a sync/fetch on mount (e.g.
  // Groups.js's room list) need this rather than a one-shot effect, since a
  // request sent while still connecting can otherwise go out ahead of the
  // connection and never get a meaningful retry - "works after a refresh"
  // was this: the refresh just gave the socket time to connect before the
  // user got around to clicking anything.
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  // username -> 'online' | 'idle' | 'dnd' (invisible users aren't listed).
  const [presenceStatuses, setPresenceStatuses] = useState({});
  // What this user picked - the status menu's value (their own entry above
  // is missing while they're invisible).
  const [myStatus, setMyStatusState] = useState('online');
  // Read by the socket handlers below (registered once) - same pattern as tRef.
  const dndRef = useRef(false);
  const [unreadFriendRequests, setUnreadFriendRequests] = useState(0);
  const [unreadGroupInvites, setUnreadGroupInvites] = useState(0);
  const [unreadMessageCounts, setUnreadMessageCounts] = useState({}); // username -> count
  const [notifications, setNotifications] = useState([]); // {id, type, text, timestamp, read}
  const [toasts, setToasts] = useState([]);
  const [groupState, dispatch] = useReducer(groupReducer, initialGroupState);
  // Read inside the socket connection effect below (whose own deps are just
  // [pushToast, pushNotification] - it must not reconnect the socket on
  // every state change), for handlers that need the CURRENT room the client
  // is sitting in at the moment an event arrives, not whatever it was when
  // the effect last ran. Same pattern as tRef just above.
  const groupStateRef = useRef(groupState);
  groupStateRef.current = groupState;

  const pushToast = useCallback((text) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, text }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushNotification = useCallback((type, text) => {
    const id = ++notificationId;
    setNotifications((prev) => [{ id, type, text, timestamp: new Date().toISOString(), read: false }, ...prev].slice(0, 50));
    if (window.cyclone && window.cyclone.notify) window.cyclone.notify(text);
  }, []);

  const markNotificationsRead = useCallback(() => {
    setNotifications((prev) => (prev.some((n) => !n.read) ? prev.map((n) => ({ ...n, read: true })) : prev));
  }, []);

  const clearFriendRequestNotifications = useCallback(() => setUnreadFriendRequests(0), []);
  const clearGroupInviteNotifications = useCallback(() => setUnreadGroupInvites(0), []);

  const markConversationRead = useCallback((username) => {
    setUnreadMessageCounts((prev) => {
      if (!prev[username]) return prev;
      const next = { ...prev };
      delete next[username];
      return next;
    });
  }, []);

  useEffect(() => {
    const username = localStorage.getItem('username');
    if (!username) return undefined;

    let cancelled = false;
    let socket = null;

    (async () => {
      // Proactive: never open the socket with whatever accessToken happens
      // to already be sitting in localStorage - if the app was closed/idle
      // long enough for it to go stale, the server's handshake middleware
      // won't reject the connection (see ackEmit's comment above), it just
      // silently leaves it unauthenticated, and every room/group fetch
      // would fail with NOT_AUTHENTICATED until something (a page reload)
      // happened to refresh the token first. Same expired->refresh pattern
      // httpClient.js's apiRequest already uses for REST calls.
      const refreshed = await RefreshToken();
      if (cancelled) return;
      if (refreshed === 'expired') {
        redirectToLogin();
        return;
      }

      socket = io(server_address, {
        transports: ['websocket'],
        // A callback, not a fixed object - re-read on every (re)connection
        // attempt (including socket.io's own automatic ones, and the
        // explicit disconnect()+connect() ackEmit does above on a stale
        // token) so it always sends whatever's currently in localStorage
        // instead of the token this effect happened to see at mount time.
        auth: (cb) => cb({ token: localStorage.getItem('accessToken') })
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        // The server takes the name from the handshake token, not this.
        socket.emit('addUser');
        setConnected(true);
      });
      socket.on('disconnect', () => setConnected(false));

      // The server already killed the HTTP session (ban/force-logout both
      // revoke every refresh token before this fires) and force-closed this
      // socket right behind the event - the redirect just needs a moment's
      // delay so the toast is actually visible before the page navigates away.
      socket.on('account:banned', ({ message } = {}) => {
        pushToast(message || tRef.current('errors.generic'));
        setTimeout(() => redirectToLogin(), 1500);
      });
      socket.on('account:forceLogout', ({ message } = {}) => {
        pushToast(message || tRef.current('errors.generic'));
        setTimeout(() => redirectToLogin(), 1500);
      });

      socket.on('admin:announcement', ({ message } = {}) => {
        if (message) pushToast(message);
      });

      socket.on('getUsers', (users) => {
        setOnlineUsers(users);
      });
      socket.on('presence:statuses', (map) => setPresenceStatuses(map || {}));
      socket.on('presence:self', ({ status }) => setMyStatusState(status || 'online'));

      // Do not disturb: incoming notifications still land in the
      // notifications list and the unread badges, they just don't pop up.
      const notifyToast = (text) => { if (!dndRef.current) pushToast(text); };

      socket.on('getMessage', ({ senderId, text }) => {
        setUnreadMessageCounts((prev) => ({ ...prev, [senderId]: (prev[senderId] || 0) + 1 }));
        const messageText = `${senderId}: ${text}`;
        notifyToast(messageText);
        pushNotification('message', messageText);
      });

      socket.on('friendRequestReceived', ({ username: from }) => {
        setUnreadFriendRequests((prev) => prev + 1);
        const requestText = tRef.current('toasts.friendRequestReceived', { name: from });
        notifyToast(requestText);
        pushNotification('friendRequest', requestText);
      });

      socket.on('friendRequestAccepted', ({ username: by }) => {
        const acceptedText = tRef.current('toasts.friendRequestAccepted', { name: by });
        notifyToast(acceptedText);
        pushNotification('friendAccepted', acceptedText);
      });

      socket.on('group:inviteReceived', ({ groupName, inviter }) => {
        setUnreadGroupInvites((prev) => prev + 1);
        const inviteText = tRef.current('toasts.groupInviteReceived', { name: inviter.username, group: groupName });
        notifyToast(inviteText);
        pushNotification('groupInvite', inviteText);
      });

      // ===== Group / room / knock =====

      socket.on('room:occupantJoined', ({ roomId, user }) => dispatch({ type: 'OCCUPANT_JOINED', roomId, user }));
      socket.on('room:occupantLeft', ({ roomId, userId }) => dispatch({ type: 'OCCUPANT_LEFT', roomId, userId }));
      socket.on('room:availabilityChanged', ({ roomId, availability }) => dispatch({ type: 'AVAILABILITY_CHANGED', roomId, availability }));
      socket.on('room:doorStateChanged', ({ roomId, doorState }) => dispatch({ type: 'DOOR_STATE_CHANGED', roomId, doorState }));
      socket.on('room:closed', ({ roomId }) => dispatch({ type: 'ROOM_CLOSED', roomId }));

      socket.on('room:youWereKicked', ({ roomId }) => {
        dispatch({ type: 'YOU_WERE_KICKED', roomId });
        pushToast(tRef.current('toasts.removedFromRoom'));
      });

      // Re-fetch rather than hand-rolling a room/group into state: a room's
      // availability/owner/occupants depend on live presence and DB lookups
      // this event doesn't carry, and guessing at them races against the
      // member's own connection sequence. Declared up here (not just further
      // down for group:memberJoined/Left) so room:resynced below can use it
      // too.
      const resyncGroup = (groupId) => {
        ackEmit(socketRef, 'presence:sync', { groupId }, tRef.current).then((res) => {
          if (res.ok) dispatch({ type: 'GROUP_SNAPSHOT', groupId, rooms: res.snapshot.rooms });
        });
      };

      // Reconnected within the grace window while still holding a room - the
      // server doesn't re-issue a voice token here (sockets reconnecting is
      // independent of the token's own ~10min lifetime), refresh proactively
      // so it's never stale right after resyncing. Also the one path where a
      // client can end up "in" a room without ever having browsed to its
      // group in Groups.js first (a page reload/relaunch wipes roomsByGroup
      // but not server-side presence) - resyncGroup fills that gap so e.g.
      // VoiceRoomPanel's roomLabel() isn't stuck on the generic fallback for
      // a room it can't find yet.
      socket.on('room:resynced', ({ room, occupants, musicState, videoWatchState, transcriptState }) => {
        dispatch({ type: 'ENTERED_ROOM', roomId: room._id, voiceToken: null, occupants });
        if (musicState) dispatch({ type: 'MUSIC_STATE_CHANGED', musicState });
        if (videoWatchState) dispatch({ type: 'VIDEO_WATCH_STATE_CHANGED', videoWatchState });
        if (transcriptState) dispatch({ type: 'TRANSCRIPT_STATE_CHANGED', transcriptState });
        resyncGroup(room.groupId);
        ackEmit(socketRef, 'room:refreshVoiceToken', {}, tRef.current).then((res) => {
          if (res.ok) dispatch({ type: 'VOICE_TOKEN_REFRESHED', voiceToken: res.voiceToken });
        });
      });

      socket.on('room:kicked_other_device', () => {
        dispatch({ type: 'LEFT_ROOM' });
        pushToast(tRef.current('toasts.kickedOtherDevice'));
      });

      // Broadcast to everyone currently in the room (see room:${roomId} in
      // socketServer.js) whenever the music bot's playback state changes -
      // the reducer ignores this if it's not for the room we're currently in
      // (a late event arriving just after leaving, etc).
      socket.on('music:stateChanged', (musicState) => dispatch({ type: 'MUSIC_STATE_CHANGED', musicState }));

      socket.on('room:reactionReceived', (reaction) => {
        reactionListenersRef.current.forEach((listener) => listener(reaction));
      });

      // Same broadcast pattern as music:stateChanged, for the independent
      // "watch together" video feature.
      socket.on('video:stateChanged', (videoWatchState) => dispatch({ type: 'VIDEO_WATCH_STATE_CHANGED', videoWatchState }));

      // ===== Transcription bot ===== Same broadcast pattern as
      // music:stateChanged, for the independent transcription feature (see
      // Backend/services/transcriptBot/).
      socket.on('transcript:stateChanged', (transcriptState) => dispatch({ type: 'TRANSCRIPT_STATE_CHANGED', transcriptState }));
      socket.on('transcript:segment', (segment) => dispatch({ type: 'TRANSCRIPT_SEGMENT', ...segment }));
      socket.on('transcript:partial', (partial) => dispatch({ type: 'TRANSCRIPT_PARTIAL', ...partial }));
      // Not reflected into groupState - TranscriptHistoryModal re-fetches
      // the session list from the REST API when opened, so there is nothing
      // for the reducer to hold onto here. Only surfaced when it is for the
      // room this client is currently sitting in (a summary finishing for a
      // room browsed away from long ago should not interrupt anything).
      socket.on('transcript:summaryReady', ({ roomId, status }) => {
        if (roomId !== groupStateRef.current.currentRoomId) return;
        if (status === 'ready') notifyToast(tRef.current('transcript.summaryReady'));
      });

      socket.on('knock:received', (knock) => {
        // Server sends a duration (ttlMs), not an absolute deadline - stamp
        // the deadline off this device's own clock so the countdown is
        // correct regardless of any clock drift between it and the server.
        dispatch({ type: 'KNOCK_RECEIVED', knock: { ...knock, expiresAt: Date.now() + knock.ttlMs } });
        startKnockLoop(knock.knockId);
        const knockText = tRef.current('knock.knockingOnDoor', { name: knock.from.username });
        notifyToast(knockText);
        pushNotification('knock', knockText);
      });
      // These four cover every way a knock can end on either side (the
      // door owner accepting/rejecting is handled locally in respondKnock
      // below, since the server never bounces that back as an event) - each
      // one has to stop the loop since only ONE of these fires per knock.
      socket.on('knock:accepted', ({ knockId, roomId, voiceToken, musicState, videoWatchState, transcriptState }) => {
        stopKnockLoop(knockId);
        dispatch({ type: 'KNOCK_RESOLVED', knockId, status: 'accepted', roomId });
        dispatch({ type: 'ENTERED_ROOM', roomId, voiceToken });
        if (musicState) dispatch({ type: 'MUSIC_STATE_CHANGED', musicState });
        if (videoWatchState) dispatch({ type: 'VIDEO_WATCH_STATE_CHANGED', videoWatchState });
        if (transcriptState) dispatch({ type: 'TRANSCRIPT_STATE_CHANGED', transcriptState });
      });
      socket.on('knock:rejected', ({ knockId, roomId }) => {
        stopKnockLoop(knockId);
        dispatch({ type: 'KNOCK_RESOLVED', knockId, status: 'rejected', roomId });
      });
      socket.on('knock:expired', ({ knockId, roomId }) => {
        stopKnockLoop(knockId);
        dispatch({ type: 'KNOCK_RESOLVED', knockId, status: 'expired', roomId });
      });
      socket.on('knock:cancelled', ({ knockId, roomId }) => {
        stopKnockLoop(knockId);
        dispatch({ type: 'KNOCK_RESOLVED', knockId, status: 'cancelled', roomId });
      });

      socket.on('group:memberJoined', ({ groupId, user }) => {
        resyncGroup(groupId);
        if (user.username !== username) notifyToast(tRef.current('toasts.joinedGroup', { name: user.username }));
      });
      socket.on('group:memberLeft', ({ groupId }) => resyncGroup(groupId));

      // The owner removed *this* client from a group - unlike memberLeft
      // (which just needs a resync since the group is still ours) there's
      // nothing left to resync, so drop it from local state outright.
      socket.on('group:youWereRemoved', ({ groupId }) => {
        dispatch({ type: 'GROUP_MEMBERSHIP_REMOVED', groupId });
        pushToast(tRef.current('toasts.removedFromGroup'));
      });
      // The owner deleted the group. The owner's own client handles it from
      // the request's answer, so only the others get the toast.
      socket.on('group:deleted', ({ groupId, name, by }) => {
        dispatch({ type: 'GROUP_MEMBERSHIP_REMOVED', groupId });
        if (by !== username) pushToast(tRef.current('toasts.groupDeleted', { name }));
      });
    })();

    return () => {
      cancelled = true;
      if (socket) {
        socket.disconnect();
        socketRef.current = null;
        setConnected(false);
        stopAllKnockLoops();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushToast, pushNotification]);

  const syncGroup = useCallback(async (groupId) => {
    const res = await ackEmit(socketRef, 'presence:sync', { groupId }, tRef.current);
    if (res.ok) dispatch({ type: 'GROUP_SNAPSHOT', groupId, rooms: res.snapshot.rooms });
    return res;
  }, []);

  const leaveRoom = useCallback(async () => {
    const res = await ackEmit(socketRef, 'room:leave', {}, tRef.current);
    if (res.ok) dispatch({ type: 'LEFT_ROOM' });
    return res;
  }, []);

  const joinRoom = useCallback(async (roomId) => {
    // Switching directly from one room to another (without an explicit
    // leave step) left the LiveKit connection pointed at the old room's
    // session while the server had already torn that participant down as
    // part of the move (see enterRoom in socketServer.js) - useVoiceConnection
    // never noticed since `active`/voiceToken presence stayed true the whole
    // time, so it kept the stale connection instead of reconnecting. Leaving
    // first forces `active` through a real false->true transition so that
    // hook tears down and reconnects cleanly.
    if (groupState.currentRoomId && groupState.currentRoomId !== roomId) {
      await leaveRoom();
    }
    const res = await ackEmit(socketRef, 'room:join', { roomId }, tRef.current);
    if (res.ok) {
      dispatch({ type: 'ENTERED_ROOM', roomId: res.room._id, voiceToken: res.voiceToken, occupants: res.occupants });
      if (res.musicState) dispatch({ type: 'MUSIC_STATE_CHANGED', musicState: res.musicState });
      if (res.videoWatchState) dispatch({ type: 'VIDEO_WATCH_STATE_CHANGED', videoWatchState: res.videoWatchState });
      if (res.transcriptState) dispatch({ type: 'TRANSCRIPT_STATE_CHANGED', transcriptState: res.transcriptState });
    }
    return res;
  }, [groupState.currentRoomId, leaveRoom]);

  const setDoorState = useCallback((roomId, doorState) => ackEmit(socketRef, 'room:setDoorState', { roomId, doorState }, tRef.current), []);
  const kickFromRoom = useCallback((roomId, userId) => ackEmit(socketRef, 'room:kick', { roomId, userId }, tRef.current), []);
  const banFromRoom = useCallback((roomId, userId) => ackEmit(socketRef, 'room:ban', { roomId, userId }, tRef.current), []);
  const unbanFromRoom = useCallback((roomId, userId) => ackEmit(socketRef, 'room:unban', { roomId, userId }, tRef.current), []);

  const sendKnock = useCallback(async (roomId) => {
    const res = await ackEmit(socketRef, 'knock:send', { roomId }, tRef.current);
    // See knock:received above - ttlMs, not an absolute expiresAt.
    if (res.ok) {
      dispatch({ type: 'KNOCK_SENT', knockId: res.knockId, roomId, expiresAt: Date.now() + res.ttlMs });
      startKnockLoop(res.knockId);
    }
    return res;
  }, []);
  // Cancelling is the one terminal path the server doesn't echo back to the
  // canceller as an event (knock:cancelled only reaches the door owner), so
  // the loop has to be stopped here directly instead of in a socket.on above.
  const cancelKnock = useCallback(async (knockId) => {
    const res = await ackEmit(socketRef, 'knock:cancel', { knockId }, tRef.current);
    if (res.ok) {
      stopKnockLoop(knockId);
      dispatch({ type: 'KNOCK_RESOLVED', knockId, status: 'cancelled', roomId: null });
    }
    return res;
  }, []);
  // Same story as cancelKnock: the owner accepting/rejecting their own
  // incoming knock resolves it locally, with no server event bouncing back
  // to stop this side's loop.
  const respondKnock = useCallback(async (knockId, accept) => {
    const res = await ackEmit(socketRef, 'knock:respond', { knockId, accept }, tRef.current);
    if (res.ok) {
      stopKnockLoop(knockId);
      dispatch({ type: 'KNOCK_RESOLVED', knockId, status: accept ? 'accepted' : 'rejected', roomId: null });
      // Accepting sends the owner (us) into their own room's voice too -
      // voiceToken is only present when that actually happened (not already
      // in there some other way).
      if (accept && res.voiceToken) {
        dispatch({ type: 'ENTERED_ROOM', roomId: res.roomId, voiceToken: res.voiceToken, occupants: res.occupants });
        // ENTERED_ROOM already clears the room we were just in's music/
        // video-watch/transcript state - these repopulate it for the room
        // we just entered, same as room:join's ack/room:resynced/
        // knock:accepted above. Missing this meant the owner's own client
        // never learned their room already had e.g. a watch-together
        // session going until the next unrelated *_STATE_CHANGED broadcast.
        if (res.musicState) dispatch({ type: 'MUSIC_STATE_CHANGED', musicState: res.musicState });
        if (res.videoWatchState) dispatch({ type: 'VIDEO_WATCH_STATE_CHANGED', videoWatchState: res.videoWatchState });
        if (res.transcriptState) dispatch({ type: 'TRANSCRIPT_STATE_CHANGED', transcriptState: res.transcriptState });
      }
    }
    return res;
  }, []);
  const refreshVoiceToken = useCallback(async () => {
    const res = await ackEmit(socketRef, 'room:refreshVoiceToken', {}, tRef.current);
    if (res.ok) dispatch({ type: 'VOICE_TOKEN_REFRESHED', voiceToken: res.voiceToken });
    return res;
  }, []);
  const clearLastKnockResult = useCallback(() => dispatch({ type: 'CLEAR_LAST_KNOCK_RESULT' }), []);

  // Plain subscriber set rather than React state: a reaction fires an
  // animation and a sound, it isn't data any component renders FROM, so
  // pushing it through state would re-render the whole tree per emoji.
  const reactionListenersRef = useRef(new Set());
  const onReaction = useCallback((listener) => {
    reactionListenersRef.current.add(listener);
    return () => reactionListenersRef.current.delete(listener);
  }, []);

  // No local dispatch on success for any of these - the server's
  // music:stateChanged broadcast is the single source of truth and reaches
  // this same client too (it's already joined room:${roomId}), same as how
  // setDoorState/kickFromRoom don't dispatch locally either.
  // enqueueMusic starts playback outright when nothing is going (the server
  // decides - see MusicSession.enqueue), so this is the only "add a track"
  // call the UI needs; there's no separate play.
  const enqueueMusic = useCallback((roomId, video) => ackEmit(socketRef, 'music:enqueue', { roomId, ...video }, tRef.current), []);
  const pauseMusic = useCallback((roomId) => ackEmit(socketRef, 'music:pause', { roomId }, tRef.current), []);
  const resumeMusic = useCallback((roomId) => ackEmit(socketRef, 'music:resume', { roomId }, tRef.current), []);
  const setMusicLoop = useCallback((roomId, loop) => ackEmit(socketRef, 'music:setLoop', { roomId, loop }, tRef.current), []);
  const stopMusic = useCallback((roomId) => ackEmit(socketRef, 'music:stop', { roomId }, tRef.current), []);
  const skipMusic = useCallback((roomId) => ackEmit(socketRef, 'music:skip', { roomId }, tRef.current), []);
  const removeFromMusicQueue = useCallback((roomId, queueId) => ackEmit(socketRef, 'music:removeFromQueue', { roomId, queueId }, tRef.current), []);
  const clearMusicQueue = useCallback((roomId) => ackEmit(socketRef, 'music:clearQueue', { roomId }, tRef.current), []);
  const moveInMusicQueue = useCallback((roomId, queueId, direction) => ackEmit(socketRef, 'music:moveInQueue', { roomId, queueId, direction }, tRef.current), []);
  const shuffleMusicQueue = useCallback((roomId) => ackEmit(socketRef, 'music:shuffleQueue', { roomId }, tRef.current), []);

  // Same no-local-dispatch rationale as the music:* functions above - the
  // server's transcript:stateChanged broadcast is the single source of truth.
  const startTranscript = useCallback((roomId) => ackEmit(socketRef, 'transcript:start', { roomId }, tRef.current), []);
  const stopTranscript = useCallback((roomId) => ackEmit(socketRef, 'transcript:stop', { roomId }, tRef.current), []);

  // Reactions are deliberately transient - nothing is dispatched into
  // groupState, they're delivered straight to whoever is listening (see
  // onReaction below) and forgotten. Keeping them out of the reducer means
  // a burst of them can't churn the state every other component reads.
  const sendReaction = useCallback((roomId, kind, value) => ackEmit(socketRef, 'room:reaction', { roomId, kind, value }, tRef.current), []);

  // Same no-local-dispatch rationale as the music:* functions above, for
  // the independent "watch together" video feature - the server's
  // video:stateChanged broadcast is the single source of truth.
  const openVideo = useCallback((roomId, video) => ackEmit(socketRef, 'video:open', { roomId, ...video }, tRef.current), []);
  const playVideo = useCallback((roomId, positionSec) => ackEmit(socketRef, 'video:play', { roomId, positionSec }, tRef.current), []);
  const pauseVideo = useCallback((roomId, positionSec) => ackEmit(socketRef, 'video:pause', { roomId, positionSec }, tRef.current), []);
  const seekVideo = useCallback((roomId, positionSec) => ackEmit(socketRef, 'video:seek', { roomId, positionSec }, tRef.current), []);
  const stopVideo = useCallback((roomId) => ackEmit(socketRef, 'video:stop', { roomId }, tRef.current), []);
  // Buffering barrier (see VideoWatchSession): reportVideoBuffering pauses
  // the room on this participant when their own player stalls,
  // reportVideoReady acks a pending barrier once they've caught up.
  const reportVideoBuffering = useCallback((roomId) => ackEmit(socketRef, 'video:buffering', { roomId }, tRef.current), []);
  const reportVideoReady = useCallback((roomId, positionSec) => ackEmit(socketRef, 'video:ready', { roomId, positionSec }, tRef.current), []);

  // Do not disturb mutes toasts (notifyToast above) and every sound effect.
  useEffect(() => {
    dndRef.current = myStatus === 'dnd';
    setSoundEffectsMuted(myStatus === 'dnd');
  }, [myStatus]);

  const setMyStatus = useCallback(async (status) => {
    const prev = myStatus;
    setMyStatusState(status);
    const res = await ackEmit(socketRef, 'presence:setStatus', { status }, tRef.current);
    if (!res || !res.ok) setMyStatusState(prev);
    return res;
  }, [myStatus]);

  // Idle: no input for IDLE_AFTER_MS on this device. The server shows
  // "idle" once every device of the user is idle (and they picked online).
  useEffect(() => {
    if (!connected) return undefined;
    const IDLE_AFTER_MS = 10 * 60 * 1000;
    let last = Date.now();
    let idle = false;
    const report = (value) => {
      idle = value;
      if (socketRef.current) socketRef.current.emit('presence:idle', { idle: value });
    };
    const onActivity = () => {
      last = Date.now();
      if (idle) report(false);
    };
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = setInterval(() => { if (!idle && Date.now() - last > IDLE_AFTER_MS) report(true); }, 30 * 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [connected]);

  // 'online' | 'idle' | 'dnd' | null (offline or invisible).
  const statusOf = useCallback((name) => presenceStatuses[name] || (onlineUsers.includes(name) ? 'online' : null), [presenceStatuses, onlineUsers]);

  return (
    <SocketContext.Provider value={{
      socket: socketRef,
      connected,
      onlineUsers,
      presenceStatuses,
      statusOf,
      myStatus,
      setMyStatus,
      unreadFriendRequests,
      clearFriendRequestNotifications,
      unreadGroupInvites,
      clearGroupInviteNotifications,
      unreadMessageCounts,
      markConversationRead,
      notifications,
      markNotificationsRead,
      toasts,
      dismissToast,
      pushToast,
      // group/room/knock
      groupState,
      syncGroup,
      joinRoom,
      leaveRoom,
      setDoorState,
      kickFromRoom,
      banFromRoom,
      unbanFromRoom,
      sendKnock,
      cancelKnock,
      respondKnock,
      refreshVoiceToken,
      clearLastKnockResult,
      enqueueMusic,
      pauseMusic,
      resumeMusic,
      setMusicLoop,
      stopMusic,
      skipMusic,
      removeFromMusicQueue,
      clearMusicQueue,
      moveInMusicQueue,
      shuffleMusicQueue,
      startTranscript,
      stopTranscript,
      sendReaction,
      onReaction,
      openVideo,
      playVideo,
      pauseVideo,
      seekVideo,
      stopVideo,
      reportVideoBuffering,
      reportVideoReady,
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
